import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { Grid } from '@mui/material';
import moment from 'moment';
import CustomPopupText from '../shared/CustomPopupText';
import CustomTextField from '../shared/CustomTextField';
import Theme from '../ui/Theme';

const ExternalProfilesCard = ({ data, handleExternalEmailChange }) => {
  const [expanded, setExpanded] = React.useState('panel1');

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };
  const handleSwitchClick = (event) => {
    event.stopPropagation();
  };

  return (
    <div>
      <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <StyledTypography component="span">{data?.title}</StyledTypography>
          <span
            role="button"
            tabIndex={0}
            onClick={handleSwitchClick}
            aria-label="ant design"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleSwitchClick(e);
            }}
            style={{ marginLeft: 'auto', cursor: 'pointer' }}
          >
            <AntSwitch
              id={data?.id}
              name="enabledProfile"
              inputProps={{ 'aria-label': 'ant design' }}
              size="large"
              defaultChecked={data?.enabled}
              onChange={handleExternalEmailChange}
            />
          </span>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="column">
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                  <CustomPopupText text="First Name" required />
                  <CustomTextField name="firstName" defaultValue={data?.firstName} width="100%" disabled />
                </Grid>
                <Grid item xs={6}>
                  <CustomPopupText text="Last Name" required />
                  <CustomTextField name="lastName" defaultValue={data?.lastName} width="100%" disabled />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                  <CustomPopupText text="External Email" required />
                  <CustomTextField
                    id={data?.id}
                    name="externalEmail"
                    handleChange={handleExternalEmailChange}
                    defaultValue={data?.email}
                    width="100%"
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomPopupText text="External Role" required />
                  <CustomTextField name="externalRole" defaultValue={data?.externalRole} width="100%" disabled />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                  <CustomPopupText text="Dealer ID" required />
                  <CustomTextField name="title" defaultValue={data?.dealerId} width="100%" disabled />
                </Grid>
                <Grid item xs={6}>
                  <CustomPopupText text="External ID" required />
                  <CustomTextField name="title" defaultValue={data?.externalId} width="100%" disabled />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                  <CustomPopupText text="Profile Created At" required />
                  <CustomTextField
                    name="createdAt"
                    defaultValue={moment(data?.createdAt).format('YYYY-MM-DD hh:mm a')}
                    width="100%"
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomPopupText text="Profile Updated At" required />
                  <CustomTextField
                    name="updatedAt"
                    defaultValue={moment(data?.updatedAt).format('YYYY-MM-DD hh:mm a')}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ArrowDropUpIcon sx={{ fontSize: '1.5rem' }} />} {...props} />
))(({ theme }) => ({
  height: '56px',
  padding: '16px',
  justifyContent: 'space-between',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(180deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AntSwitch = styled(Switch)(({ theme }) => ({
  marginLeft: 'auto',
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: Theme.palette.primary.main,
        ...theme.applyStyles('dark', {
          backgroundColor: Theme.palette.primary.main,
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
    ...theme.applyStyles('dark', {
      backgroundColor: 'rgba(255,255,255,.35)',
    }),
  },
}));

const StyledTypography = styled(Typography)(() => ({
  fontFamily: 'Montserrat',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '19.6px',
}));
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

export default ExternalProfilesCard;
